import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared';
import { CategoryPills } from '~/ui-rocco/pages/home-page/components/CategoryPills';

interface Credit {
    name: string;
    link?: string;
}

// "Tags" / "Project team" labels — Figma 2354:55678 is SF Pro Semibold (590);
// font-sans (Neue Haas) has no 600 face, so use the real 500 cut (matches the
// intro caption), 15px, tracking -0.05em, #9a9a9a.
const labelClass =
    'm-0 font-sans font-medium tracking-[-0.05em] text-[#9a9a9a] max-md:text-sm md:text-[0.9375rem]';

/**
 * P4 — meta row under the summary: a "Tags" column (search tags as outline
 * pills) beside a "Project team" column (the credited professionals). Each
 * column hides when empty; the whole row hides when neither has content.
 */
export const ProjectCreditsRow = () => {
    const { project, projectTeamMembers } = useLoaderData<typeof loader>();
    const { Professional } = project;

    const leadName = Professional.Title ?? '';
    const tags = (project.SearchTags ?? [])
        .map((tag) => tag?.Text)
        .filter((text): text is string => !!text && text !== leadName);

    // Build the credited team from the structured sources we have, deduped by
    // name: the lead professional, any project-group team members, and the
    // project's photographers.
    const credits: Credit[] = [];
    const seen = new Set<string>();
    const addCredit = (name?: string | null, link?: string | null) => {
        const trimmed = (name ?? '').trim();
        const key = trimmed.toLowerCase();
        if (!trimmed || seen.has(key)) return;
        seen.add(key);
        credits.push({ name: trimmed, link: link || undefined });
    };

    addCredit(leadName, Professional.Link);
    for (const group of projectTeamMembers ?? []) {
        for (const member of group.TeamMembers ?? []) {
            addCredit(member.ProfessionalName || member.Name, member.Link);
        }
    }
    for (const photographer of project.Photographers ?? []) {
        addCredit(photographer.Title, photographer.Link);
    }

    if (tags.length === 0 && credits.length === 0) return null;

    return (
        <section className="bg-transparent max-md:px-6 md:px-[3rem]">
            <div
                className={cn(
                    CONTENT_CAP_PADDED,
                    'flex max-md:flex-col max-md:gap-8 md:gap-[9rem]',
                )}
            >
                {tags.length > 0 ? (
                    <div className="flex flex-col gap-3 md:w-[19.8rem] md:gap-[1.5625rem]">
                        <p className={labelClass}>Tags</p>
                        <CategoryPills
                            items={tags.map((label) => ({ label }))}
                            className="text-white"
                            // The homepage's vw-scaled pill gap is ~1px on a
                            // phone (pills touch); give the tags real breathing
                            // room and let them wrap to a second line.
                            wrapClassName="max-md:gap-x-2 max-md:gap-y-2.5"
                        />
                    </div>
                ) : null}
                {credits.length > 0 ? (
                    <div className="flex flex-col gap-3 md:w-[19.8rem] md:gap-[1.5625rem]">
                        <p className={labelClass}>Project team</p>
                        <ul className="m-0 flex list-none flex-col gap-0 p-0">
                            {credits.map((credit) => (
                                <li key={credit.name}>
                                    {/* 12px uppercase per Figma (node 2354:55689). */}
                                    {credit.link ? (
                                        <RoccoLink
                                            to={credit.link}
                                            className="font-sans leading-normal text-white uppercase transition-opacity hover:opacity-70 max-md:text-[0.75rem] md:text-[0.75rem]"
                                        >
                                            {credit.name}
                                        </RoccoLink>
                                    ) : (
                                        <span className="font-sans leading-normal text-white uppercase max-md:text-[0.75rem] md:text-[0.75rem]">
                                            {credit.name}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
            </div>
        </section>
    );
};
